import { createFileRoute } from "@tanstack/react-router"
import { useQueryClient } from "@tanstack/react-query"
import {
  backofficeSupportFiltersCacheKey,
  BackofficeSupportFiltersCache,
} from "modules/backoffice/lib/support/backofficeSupportFiltersCache"
import { BackofficeSupport } from "modules/backoffice/components/support"
import { useEffect } from "react"
import { GetBackofficeSupportQuestionsSchema } from "schema"
import { z } from "zod"

const BackofficeSupportSearchSchema = GetBackofficeSupportQuestionsSchema

function RouteComponent() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (search.filterBy) return

    const cached = queryClient.getQueryData<BackofficeSupportFiltersCache>(
      backofficeSupportFiltersCacheKey,
    )
    if (!cached?.filterBy) return

    navigate({
      replace: true,
      search: (prev) => ({
        ...prev,
        page: 1,
        filterBy: cached.filterBy,
      }),
    })
  }, [navigate, queryClient, search.filterBy])

  useEffect(() => {
    if (!search.filterBy) return

    queryClient.setQueryData<BackofficeSupportFiltersCache>(backofficeSupportFiltersCacheKey, {
      filterBy: search.filterBy,
    })
  }, [queryClient, search.filterBy])

  return (
    <BackofficeSupport
      search={search}
      routeFullPath={Route.fullPath}
      onFilterByChange={(filterBy) => {
        queryClient.setQueryData<BackofficeSupportFiltersCache>(backofficeSupportFiltersCacheKey, {
          filterBy,
        })

        navigate({
          search: (prev) => ({
            ...prev,
            page: 1,
            filterBy,
          }),
        })
      }}
      onTimeRangeChange={({ timeFrame, timeDimension }) =>
        navigate({
          search: (prev) => ({
            ...prev,
            page: 1,
            timeFrame,
            timeDimension,
          }),
        })
      }
    />
  )
}

export const Route = createFileRoute("/_base/_auth/backoffice/support/list")({
  component: RouteComponent,
  validateSearch: (search: Partial<z.input<typeof BackofficeSupportSearchSchema>>) => {
    return BackofficeSupportSearchSchema.parse(search)
  },
})
